import {
  Alert, Button, Card, Col, Descriptions, Divider, Form, Input, InputNumber, Modal,
  Row, Select, Space, Steps, Switch, Table, Tag, Tooltip, Typography, Upload
} from 'antd';
import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, DollarCircleOutlined,
  DownloadOutlined, EditOutlined, EyeOutlined, HistoryOutlined, MailOutlined,
  MinusCircleOutlined, PlusOutlined, UploadOutlined
} from '@ant-design/icons';
import { apiCall } from '../../API/RestApi';
import { useI18n } from '../../Utils/i18n';
import { modalError } from '../../Component/Modal/ModalLayout';
import { useSelector } from 'react-redux';
import moment from 'moment';
import * as XLSX from 'xlsx-js-style';
import { currencyFormat } from '../../Utils/formatCurrency';
import { getYearOptions } from '../../Utils/getYears';
import { toTerbilang } from '../../Utils/terbilang';
import { moneyInputProps } from '../../Utils/numberFormat';
import { exportSalaryPdf } from './excel/ExportSalaryPdf';

const { Title, Text, Paragraph } = Typography;
const MONTHS = moment.months();

const componentAmount = (record, name) => Number((record?.pay_details || []).find(x => String(x?.pay_title?.name || '').toLowerCase() === name.toLowerCase())?.allowance || 0);
const overtimeHoursFromRecord = (record) => {
  const row = (record?.pay_details || []).find(x => String(x?.pay_title?.name || '').toLowerCase() === 'lembur');
  const match = String(row?.desc || '').match(/([\d.]+)\s*jam/i);
  return match ? Number(match[1]) : 0;
};

function Payroll({ detailGroupId = null }) {
  const token = useSelector((state) => state.user.token);
  const navigate = useNavigate();
  const { t } = useI18n();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [employeePayrolls, setEmployeePayrolls] = useState([]);
  const [groupMeta, setGroupMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');
  const [monthSearch, setMonthSearch] = useState();
  const [yearSearch, setYearSearch] = useState();
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [alertText, setAlertText] = useState('');
  const [groupPage, setGroupPage] = useState({ current: 1, pageSize: 10, total: 0 });

  const [groupModal, setGroupModal] = useState(false);
  const [groupEditing, setGroupEditing] = useState(null);
  const [groupForm] = Form.useForm();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [payTitles, setPayTitles] = useState([]);
  const [wizardStep, setWizardStep] = useState(0);
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form) || {};
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);

  const [detailData, setDetailData] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [logsOpen, setLogsOpen] = useState(false);
  const [groupLogs, setGroupLogs] = useState([]);
  const [companySetting, setCompanySetting] = useState({});

  const request = async (method, url, payload, multipart = false) => apiCall({
    method, url,
    data: {
      data: payload,
      headers: {
        ...(multipart ? { 'Content-Type': 'multipart/form-data' } : { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${token}`
      }
    }
  });

  const estimated = useMemo(() => {
    const base = Number(formValues.base_salary || 0);
    const fixed = Number(formValues.fixed_allowance || 0);
    const other = Number(formValues.other_allowance || 0);
    const overtime = Number(formValues.overtime_hours || 0) * Number(formValues.overtime_rate || 0);
    const extras = Array.isArray(formValues.components) ? formValues.components : [];
    const extraBenefits = extras.filter(x => x?.category !== 'cost').reduce((a, x) => a + Number(x?.amount || 0), 0);
    const extraCosts = extras.filter(x => x?.category === 'cost').reduce((a, x) => a + Number(x?.amount || 0), 0);
    const gross = base + fixed + other + overtime + extraBenefits;
    const deductions = Number(formValues.bpjs_health || 0) + Number(formValues.bpjs_employment || 0) + Number(formValues.other_deduction || 0) + extraCosts + (formValues.auto_pph21 === false ? Number(formValues.pph21 || 0) : 0);
    return { gross, deductions, netBeforeAutoTax: Math.max(0, gross - deductions), overtime };
  }, [formValues]);

  const loadGroups = async (overrides = {}) => {
    setGroupLoading(true);
    const page = overrides.current ?? groupPage.current;
    const limit = overrides.pageSize ?? groupPage.pageSize;
    const search = overrides.search ?? groupSearch;
    const month = overrides.month ?? monthSearch;
    const year = overrides.year ?? yearSearch;
    const res = await request('GET', `/payroll-groups?page=${page}&limit=${limit}&search=${encodeURIComponent(search || '')}&month=${encodeURIComponent(month || '')}&year=${year || ''}`);
    setGroupLoading(false);
    if (res?.status === 'success') {
      setGroups(res.data?.data || []);
      setGroupPage(prev => ({ ...prev, current: page, pageSize: limit, total: res.data?.total_data || 0 }));
    }
  };

  const loadGroupEmployees = async (group = selectedGroup, search = employeeSearch) => {
    if (!group?.payroll_group_id) { setEmployeePayrolls([]); return; }
    setLoading(true);
    const res = await request('GET', `/payroll-groups/${group.payroll_group_id}/payrolls?search=${encodeURIComponent(search || '')}`);
    setLoading(false);
    if (res?.status === 'success') {
      setSelectedGroup(res.data?.group || group);
      setEmployeePayrolls(res.data?.employees || []);
      setGroupMeta(res.data?.meta || {});
    }
  };

  const loadCompanySetting = async () => {
    const res = await request('GET', '/company-setting');
    if (res?.status === 'success') setCompanySetting(res.data || {});
  };

  const loadEmployees = async () => {
    const res = await request('GET', '/selectusers?nopage=true&limit=1000');
    if (res?.status === 'success') setEmployees(res.data?.data || []);
  };

  const loadPayTitles = async () => {
    const res = await request('GET', '/paytitle?nopage=true&limit=1000&sortBy=name&order=asc');
    if (res?.status === 'success') setPayTitles((res.data?.data || []).filter(x => x.status !== false));
  };

  useEffect(() => {
    loadEmployees(); loadPayTitles(); loadCompanySetting();
    if (detailGroupId) {
      loadGroupEmployees({ payroll_group_id: Number(detailGroupId) }, '');
    } else {
      loadGroups();
    }
  }, [detailGroupId]);

  useEffect(() => {
    if (!formOpen || !formValues.users_id || !selectedGroup) { setCalcResult(null); return undefined; }
    const timer = setTimeout(async () => {
      setCalcLoading(true);
      const payload = {
        ...form.getFieldsValue(true),
        payroll_id: editing?.payroll_id || 0,
        payroll_group_id: selectedGroup.payroll_group_id,
        month: selectedGroup.month,
        year: Number(selectedGroup.year)
      };
      const res = await request('POST', '/payroll/calculate', payload);
      setCalcLoading(false);
      if (res?.status === 'success') setCalcResult(res.data);
    }, 450);
    return () => clearTimeout(timer);
  }, [formOpen, selectedGroup?.payroll_group_id, formValues.users_id, formValues.base_salary, formValues.fixed_allowance, formValues.other_allowance, formValues.auto_overtime, formValues.overtime_hours, formValues.overtime_rate, formValues.bpjs_health, formValues.bpjs_employment, formValues.other_deduction, formValues.auto_pph21, formValues.is_last_tax_period, formValues.pph21, JSON.stringify(formValues.components || []), editing?.payroll_id]);

  const openCreateGroup = () => {
    setGroupEditing(null);
    groupForm.resetFields();
    groupForm.setFieldsValue({ month: moment().format('MMMM'), year: moment().year() });
    setGroupModal(true);
  };

  const openEditGroup = (g) => {
    setGroupEditing(g);
    groupForm.setFieldsValue({ name: g.name, month: g.month, year: Number(g.year) });
    setGroupModal(true);
  };

  const saveGroup = async (values) => {
    setLoading(true);
    const res = await request(groupEditing ? 'PUT' : 'POST', groupEditing ? `/payroll-groups/${groupEditing.payroll_group_id}` : '/payroll-groups', values);
    setLoading(false);
    if (res?.status !== 'success') return modalError(res?.message || 'Group payroll belum dapat disimpan');
    setAlertText(res.message);
    setGroupModal(false);
    if (!detailGroupId) await loadGroups({ current: 1 });
    if (groupEditing && selectedGroup?.payroll_group_id === groupEditing.payroll_group_id) {
      setSelectedGroup(res.data);
      await loadGroupEmployees(res.data);
    }
  };

  const deleteGroup = (g) => Modal.confirm({
    title: 'Hapus group payroll?',
    content: g.status === 'published' ? 'Group masih published. Unpublish terlebih dahulu.' : 'Group hanya dapat dihapus jika tidak lagi berisi employee payroll.',
    okButtonProps: { danger: true, disabled: g.status === 'published' },
    onOk: async () => {
      const res = await request('DELETE', `/payroll-groups/${g.payroll_group_id}`);
      if (res?.status !== 'success') return modalError(res?.message || 'Group payroll belum dapat dihapus');
      if (selectedGroup?.payroll_group_id === g.payroll_group_id) { setSelectedGroup(null); setEmployeePayrolls([]); }
      setAlertText(res.message); if (!detailGroupId) loadGroups();
    }
  });

  const selectGroup = async (g) => {
    setSelectedGroup(g);
    setEmployeeSearch('');
    await loadGroupEmployees(g, '');
  };

  const openAdd = () => {
    if (!selectedGroup) return modalError('Pilih group payroll terlebih dahulu.');
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      payroll_group_id: selectedGroup.payroll_group_id,
      users_id: undefined,
      month: selectedGroup.month,
      year: Number(selectedGroup.year),
      type_name: selectedGroup.name,
      auto_overtime: true,
      overtime_hours: 0,
      overtime_rate: 0,
      base_salary: 0,
      fixed_allowance: 0,
      other_allowance: 0,
      bpjs_health: 0,
      bpjs_employment: 0,
      other_deduction: 0,
      auto_pph21: true,
      is_last_tax_period: false,
      pph21: 0,
      components: []
    });
    setWizardStep(0); setCalcResult(null); setFormOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      payroll_group_id: selectedGroup.payroll_group_id,
      users_id: record.users_id,
      month: selectedGroup.month,
      year: Number(selectedGroup.year),
      type_name: record.type_name || selectedGroup.name,
      title: record.title,
      desc: record.desc,
      base_salary: componentAmount(record, 'Gaji Pokok'),
      fixed_allowance: componentAmount(record, 'Tunjangan Tetap'),
      other_allowance: componentAmount(record, 'Tunjangan Lain'),
      auto_overtime: true,
      overtime_hours: overtimeHoursFromRecord(record),
      overtime_rate: overtimeHoursFromRecord(record) > 0 ? Math.round(componentAmount(record, 'Lembur') / overtimeHoursFromRecord(record)) : 0,
      bpjs_health: componentAmount(record, 'BPJS Kesehatan'),
      bpjs_employment: componentAmount(record, 'BPJS Ketenagakerjaan'),
      other_deduction: componentAmount(record, 'Potongan Lain'),
      auto_pph21: true,
      is_last_tax_period: false,
      pph21: componentAmount(record, 'PPh 21'),
      components: (record.pay_details || []).filter(x => x.source === 'custom').map(x => ({
        code: x.code,
        name: x.pay_title?.slip_name || x.pay_title?.name,
        category: x.category || x.pay_title?.type || 'benefit',
        amount: Number(x.allowance || 0),
        taxable: !!x.taxable,
        tax_deductible: !!x.tax_deductible,
        desc: x.desc || ''
      }))
    });
    setWizardStep(0); setCalcResult(null); setFormOpen(true);
  };

  const savePayroll = async (values) => {
    const payload = { ...values, payroll_group_id: selectedGroup.payroll_group_id, month: selectedGroup.month, year: Number(selectedGroup.year) };
    setLoading(true);
    const res = await request(editing ? 'PUT' : 'POST', editing ? `/payroll/manual/${editing.payroll_id}` : '/payroll/manual', payload);
    setLoading(false);
    if (res?.status !== 'success') return modalError(res?.message || 'Employee payroll belum dapat disimpan');
    setAlertText(res.message); setFormOpen(false); setEditing(null); await loadGroupEmployees(); if (!detailGroupId) await loadGroups();
  };

  const removeEmployeePayroll = (record) => {
    if (selectedGroup?.status === 'published') return modalError('Employee payroll tidak dapat dihapus saat group masih published. Unpublish group terlebih dahulu.');
    Modal.confirm({
      title: 'Hapus employee payroll?',
      content: `${record.users?.name || 'Employee'} akan dihapus dari group payroll ini.`,
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await request('DELETE', `/payroll/${record.payroll_id}`);
        if (res?.status !== 'success') return modalError(res?.message || 'Employee payroll belum dapat dihapus');
        setAlertText(res.message); await loadGroupEmployees(); if (!detailGroupId) await loadGroups();
      }
    });
  };

  const publishGroup = (status = true) => {
    if (!selectedGroup) return;
    Modal.confirm({
      title: status ? 'Publish group payroll?' : 'Unpublish group payroll?',
      content: status
        ? `Semua ${employeePayrolls.length} employee payroll dalam ${selectedGroup.name} akan dipublish dan slip PDF otomatis dikirim ke email masing-masing employee.`
        : 'Group akan kembali menjadi draft. Setelah unpublish, employee payroll dapat ditambah, diedit, atau dihapus kembali.',
      okText: status ? 'Publish & Kirim Email' : 'Unpublish',
      okButtonProps: status ? { type: 'primary' } : { danger: true },
      onOk: async () => {
        setLoading(true);
        const res = await request('POST', `/payroll-groups/${selectedGroup.payroll_group_id}/publish`, { status });
        setLoading(false);
        if (res?.status !== 'success') return modalError(res?.message || 'Status group payroll belum dapat diperbarui');
        const emailInfo = status ? ` • Email terkirim: ${res.data?.email_sent || 0}, gagal: ${res.data?.email_failed || 0}` : '';
        setAlertText(`${res.message}${emailInfo}`);
        await loadGroupEmployees({ payroll_group_id: selectedGroup.payroll_group_id });
        if (!detailGroupId) await loadGroups();
      }
    });
  };

  const openLogs = async () => {
    if (!selectedGroup) return;
    const res = await request('GET', `/payroll-groups/${selectedGroup.payroll_group_id}/logs`);
    if (res?.status === 'success') { setGroupLogs(res.data || []); setLogsOpen(true); }
  };

  const applySheetStyle = (ws, headerCount, rowCount) => {
    const ref = XLSX.utils.decode_range(ws['!ref'] || 'A1:A1');
    for (let c = 0; c <= ref.e.c; c += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
      if (cell) cell.s = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '0B4A8B' } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: { top: { style: 'thin', color: { rgb: 'D9E2F3' } }, bottom: { style: 'thin', color: { rgb: 'D9E2F3' } }, left: { style: 'thin', color: { rgb: 'D9E2F3' } }, right: { style: 'thin', color: { rgb: 'D9E2F3' } } } };
    }
    for (let r = 1; r <= ref.e.r; r += 1) {
      for (let c = 0; c <= ref.e.c; c += 1) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (!cell) continue;
        cell.s = { alignment: { vertical: 'center', horizontal: typeof cell.v === 'number' ? 'right' : 'left' }, border: { bottom: { style: 'hair', color: { rgb: 'E5E7EB' } } }, fill: r % 2 === 0 ? { fgColor: { rgb: 'F8FAFC' } } : undefined };
        if (typeof cell.v === 'number') cell.z = '#,##0';
      }
    }
    ws['!rows'] = [{ hpt: 28 }, ...Array(Math.max(1, rowCount)).fill({ hpt: 21 })];
    ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 0, c: ref.e.c } }) };
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  };

  const downloadTemplate = () => {
    if (!selectedGroup) return modalError('Pilih group payroll terlebih dahulu.');
    const activeTitles = payTitles.filter(p => p.status !== false);
    const headers = ['No', 'Payroll Type', 'Employee Code', 'Employee Name', ...activeTitles.map(p => p.slip_name || p.name)];
    const sample = [1, selectedGroup.name, 'EMP001', 'NAMA KARYAWAN', ...activeTitles.map(() => 0)];
    const ws = XLSX.utils.aoa_to_sheet([headers, sample]);
    ws['!cols'] = headers.map((h, i) => ({ wch: i === 3 ? 30 : i >= 4 ? Math.max(16, Math.min(28, String(h).length + 3)) : Math.max(14, String(h).length + 3) }));
    applySheetStyle(ws, 4, 1);

    const guideRows = [
      ['PEDOMAN IMPORT PAYROLL', ''],
      ['Group Payroll', selectedGroup.name],
      ['Periode', `${selectedGroup.month} ${selectedGroup.year}`],
      ['', ''],
      ['1', 'Jangan mengubah nama header komponen payroll.'],
      ['2', 'Employee Code diutamakan untuk mencocokkan karyawan.'],
      ['3', 'Nominal diisi angka tanpa Rp. Contoh: 1500000.'],
      ['4', 'Kolom pendapatan menambah gaji dan kolom potongan mengurangi gaji sesuai tipe Payslip Title.'],
      ['5', 'Baris contoh dapat dihapus sebelum import.'],
    ];
    const guide = XLSX.utils.aoa_to_sheet(guideRows);
    guide['!cols'] = [{ wch: 18 }, { wch: 78 }];
    guide['A1'].s = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 14 }, fill: { fgColor: { rgb: '0B4A8B' } }, alignment: { horizontal: 'left' } };
    guide['B1'].s = guide['A1'].s;
    guide['!merges'] = [XLSX.utils.decode_range('A1:B1')];
    for (let r = 1; r < guideRows.length; r += 1) {
      ['A', 'B'].forEach(col => { if (guide[`${col}${r + 1}`]) guide[`${col}${r + 1}`].s = { alignment: { vertical: 'top', wrapText: true }, fill: r <= 2 ? { fgColor: { rgb: 'EAF2F8' } } : undefined, font: { bold: r <= 2 } }; });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payroll Import');
    XLSX.utils.book_append_sheet(wb, guide, 'Panduan');
    XLSX.writeFile(wb, `Template_Payroll_${selectedGroup.name.replace(/[^a-z0-9_-]+/gi, '_')}_${selectedGroup.month}_${selectedGroup.year}.xlsx`);
  };

  const uploadImport = async () => {
    if (!selectedGroup || !fileList.length) return modalError('Pilih group payroll dan file Excel.');
    const fd = new FormData();
    fd.append('payroll_group_id', selectedGroup.payroll_group_id);
    fd.append('file', fileList[0].originFileObj);
    setLoading(true);
    const res = await request('POST', '/payrollimportexcel', fd, true);
    setLoading(false);
    if (res?.status !== 'success') return modalError(res?.message || 'Import payroll belum berhasil');
    setAlertText(res.message); setImportOpen(false); setFileList([]); await loadGroupEmployees(); await loadGroups();
  };

  const exportGroupExcel = () => {
    if (!selectedGroup) return;
    const componentNames = [];
    employeePayrolls.forEach(p => (p.pay_details || []).forEach(d => {
      const name = d.pay_title?.slip_name || d.pay_title?.name || d.code || 'Komponen';
      if (!componentNames.includes(name)) componentNames.push(name);
    }));
    const headers = ['Employee Code', 'Employee Name', 'Group Payroll', 'Periode', ...componentNames, 'Net Salary', 'Status'];
    const rows = employeePayrolls.map(p => {
      const detailMap = {};
      (p.pay_details || []).forEach(d => { detailMap[d.pay_title?.slip_name || d.pay_title?.name || d.code || 'Komponen'] = Number(d.allowance || 0); });
      return [p.users?.users_code || '', p.users?.name || '-', selectedGroup.name, `${selectedGroup.month} ${selectedGroup.year}`, ...componentNames.map(name => detailMap[name] || 0), Number(p.net_salary || 0), selectedGroup.status === 'published' ? 'Published' : 'Draft'];
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map((h, i) => ({ wch: i === 1 ? 28 : i === 2 ? 30 : i >= 4 && i < headers.length - 1 ? 18 : Math.max(13, Math.min(20, String(h).length + 3)) }));
    applySheetStyle(ws, 4, rows.length);
    const totalRow = rows.length + 2;
    XLSX.utils.sheet_add_aoa(ws, [['', '', '', 'TOTAL', ...componentNames.map(() => ''), employeePayrolls.reduce((sum, p) => sum + Number(p.net_salary || 0), 0), '']], { origin: `A${totalRow}` });
    for (let c = 0; c < headers.length; c += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: totalRow - 1, c })];
      if (cell) cell.s = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '334155' } }, alignment: { horizontal: c >= 4 ? 'right' : 'left' } };
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employee Payroll');
    XLSX.writeFile(wb, `${selectedGroup.name.replace(/[^a-z0-9_-]+/gi, '_')}_${selectedGroup.month}_${selectedGroup.year}.xlsx`);
  };

  const exportSlip = (record) => exportSalaryPdf({
    ...record,
    users: record.users || {},
    pay_details: (record.pay_details || []).map(item => ({ ...item, pay_title: { ...item.pay_title, name: item.pay_title?.slip_name || item.pay_title?.name || item.code || 'Komponen' } }))
  }, companySetting);

  const groupColumns = [
    { title: 'Group Payroll', dataIndex: 'name', render: (v, r) => <Button type="link" style={{ padding: 0, fontWeight: 600 }} onClick={() => navigate(`/payrollemployee/group/${r.payroll_group_id}`)}>{v}</Button> },
    { title: 'Periode', render: (_, r) => `${r.month} ${r.year}` },
    { title: 'Employee', dataIndex: 'employee_count', align: 'center' },
    { title: 'Total Net', dataIndex: 'total_net', render: currencyFormat },
    { title: 'Status', dataIndex: 'status', render: v => <Tag color={v === 'published' ? 'green' : 'orange'}>{String(v || 'draft').toUpperCase()}</Tag> },
    { title: 'Dibuat', dataIndex: 'created_at', render: v => v ? moment(v).format('DD MMM YYYY HH:mm') : '-' },
    { title: 'Dipublish', dataIndex: 'published_at', render: v => v ? moment(v).format('DD MMM YYYY HH:mm') : '-' },
    { title: 'Di-unpublish', dataIndex: 'unpublished_at', render: v => v ? moment(v).format('DD MMM YYYY HH:mm') : '-' },
    { title: 'Aksi', width: 160, render: (_, r) => <Space><Tooltip title="Lihat detail"><Button icon={<EyeOutlined />} onClick={() => navigate(`/payrollemployee/group/${r.payroll_group_id}`)} /></Tooltip><Tooltip title="Edit group"><Button icon={<EditOutlined />} onClick={() => openEditGroup(r)} /></Tooltip><Tooltip title="Hapus group"><Button danger icon={<DeleteOutlined />} onClick={() => deleteGroup(r)} disabled={r.status === 'published'} /></Tooltip></Space> }
  ];

  const employeeColumns = [
    { title: 'Employee', render: (_, r) => <div><Text strong>{r.users?.name || '-'}</Text><div><Text type="secondary">{r.users?.users_code || '-'}</Text></div></div> },
    { title: 'Judul Slip', dataIndex: 'title' },
    { title: 'Net Salary', dataIndex: 'net_salary', render: currencyFormat },
    { title: 'Updated', dataIndex: 'updated_at', render: v => v ? moment(v).format('DD MMM YYYY HH:mm') : '-' },
    { title: 'Aksi', width: 170, render: (_, r) => <Space><Tooltip title="Detail"><Button icon={<EyeOutlined />} onClick={() => { setDetailData(r); setDetailOpen(true); }} /></Tooltip><Tooltip title="Edit"><Button icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip><Tooltip title={selectedGroup?.status === 'published' ? 'Unpublish group terlebih dahulu' : 'Hapus'}><Button danger icon={<DeleteOutlined />} disabled={selectedGroup?.status === 'published'} onClick={() => removeEmployeePayroll(r)} /></Tooltip></Space> }
  ];

  return <div>
    <Helmet><title>Payroll Group</title></Helmet>
    {alertText && <Alert showIcon closable type="success" message={alertText} style={{ marginBottom: 16 }} onClose={() => setAlertText('')} />}

    {!detailGroupId && <>
    <Card>
      <Row gutter={[12, 12]} justify="space-between" align="middle">
        <Col><Title level={4} style={{ margin: 0 }}>Payroll Group</Title><Text type="secondary">Kelola payroll berdasarkan group dan periode.</Text></Col>
        <Col><Button type="primary" icon={<PlusOutlined />} onClick={openCreateGroup}>Tambah Group Payroll</Button></Col>
      </Row>
      <Divider />
      <Row gutter={[8, 8]}>
        <Col xs={24} md={10}><Input.Search value={groupSearch} onChange={e => setGroupSearch(e.target.value)} enterButton={t('search')} placeholder="Cari nama group payroll" onSearch={() => loadGroups({ current: 1, search: groupSearch, month: monthSearch, year: yearSearch })} /></Col>
        <Col xs={12} md={5}><Select allowClear value={monthSearch} onChange={(value) => { setMonthSearch(value); loadGroups({ current: 1, month: value, year: yearSearch, search: groupSearch }); }} options={MONTHS.map(m => ({ value: m, label: m }))} placeholder="Bulan" style={{ width: '100%' }} /></Col>
        <Col xs={12} md={5}><Select allowClear value={yearSearch} onChange={(value) => { setYearSearch(value); loadGroups({ current: 1, year: value, month: monthSearch, search: groupSearch }); }} options={getYearOptions().map(y => ({ value: Number(y), label: y }))} placeholder="Tahun" style={{ width: '100%' }} /></Col>
        <Col xs={24} md={4}><Button block onClick={() => { setGroupSearch(''); setMonthSearch(undefined); setYearSearch(undefined); loadGroups({ current: 1, search: '', month: '', year: '' }); }}>Reset</Button></Col>
      </Row>
      <Table style={{ marginTop: 16 }} rowKey="payroll_group_id" loading={groupLoading} columns={groupColumns} dataSource={groups} scroll={{ x: 1050 }} pagination={groupPage} onChange={(p) => loadGroups({ current: p.current, pageSize: p.pageSize })} rowClassName={r => selectedGroup?.payroll_group_id === r.payroll_group_id ? 'payroll-group-selected-row' : ''} />
    </Card>

    </>}

    {selectedGroup && <Card style={{ marginTop: detailGroupId ? 0 : 16 }}>
      {detailGroupId && <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/payrollemployee')} style={{ marginBottom: 16 }}>Kembali ke Payroll Group</Button>}
      <Row gutter={[12, 12]} justify="space-between" align="top">
        <Col xs={24} lg={13}>
          <Title level={3} style={{ marginBottom: 4 }}>{selectedGroup.name}</Title>
          <Text type="secondary">Detail group payroll, employee payroll, status publish, dan riwayat aktivitas.</Text>
        </Col>
        <Col xs={24} lg={11} style={{ display: 'flex', justifyContent: 'flex-end' }}><Space wrap>
          <Button icon={<HistoryOutlined />} onClick={openLogs}>Riwayat</Button>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>Template Import</Button>
          <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)} disabled={selectedGroup.status === 'published'}>Import Excel</Button>
          <Button icon={<DownloadOutlined />} onClick={exportGroupExcel}>Export</Button>
          {selectedGroup.status === 'published'
            ? <Button danger onClick={() => publishGroup(false)}>Unpublish</Button>
            : <Button type="primary" icon={<MailOutlined />} onClick={() => publishGroup(true)}>Publish</Button>}
        </Space></Col>
      </Row>
      <Divider />
      <Descriptions bordered size="small" column={{ xs: 1, md: 2, lg: 3 }} style={{ marginBottom: 18 }}>
        <Descriptions.Item label="Nama Group">{selectedGroup.name || '-'}</Descriptions.Item>
        <Descriptions.Item label="Periode">{selectedGroup.month} {selectedGroup.year}</Descriptions.Item>
        <Descriptions.Item label="Status"><Tag color={selectedGroup.status === 'published' ? 'green' : 'orange'}>{String(selectedGroup.status || 'draft').toUpperCase()}</Tag></Descriptions.Item>
        <Descriptions.Item label="Jumlah Employee">{groupMeta.employee_count ?? employeePayrolls.length}</Descriptions.Item>
        <Descriptions.Item label="Total Net Salary">{currencyFormat(groupMeta.total_net ?? employeePayrolls.reduce((sum, item) => sum + Number(item.net_salary || 0), 0))}</Descriptions.Item>
        <Descriptions.Item label="Dibuat Oleh">{groupMeta.created_by_name || '-'}</Descriptions.Item>
        <Descriptions.Item label="Dibuat">{selectedGroup.created_at ? moment(selectedGroup.created_at).format('DD MMM YYYY HH:mm:ss') : '-'}</Descriptions.Item>
        <Descriptions.Item label="Diperbarui Oleh">{groupMeta.updated_by_name || '-'}</Descriptions.Item>
        <Descriptions.Item label="Terakhir Diperbarui">{selectedGroup.updated_at ? moment(selectedGroup.updated_at).format('DD MMM YYYY HH:mm:ss') : '-'}</Descriptions.Item>
        <Descriptions.Item label="Dipublish Oleh">{groupMeta.published_by_name || '-'}</Descriptions.Item>
        <Descriptions.Item label="Dipublish">{selectedGroup.published_at ? moment(selectedGroup.published_at).format('DD MMM YYYY HH:mm:ss') : '-'}</Descriptions.Item>
        <Descriptions.Item label="Di-unpublish">{selectedGroup.unpublished_at ? moment(selectedGroup.unpublished_at).format('DD MMM YYYY HH:mm:ss') : '-'}</Descriptions.Item>
      </Descriptions>
      {selectedGroup.status === 'published' && <Alert type="success" showIcon style={{ marginBottom: 16 }} message="Group payroll sudah dipublish" description="Slip gaji sudah tersedia untuk employee dan email slip PDF telah diproses saat publish. Unpublish group bila Anda perlu mengubah data employee payroll." />}

      <Row gutter={[8, 8]} justify="space-between"><Col xs={24} md={12}><Input.Search value={employeeSearch} onChange={e => setEmployeeSearch(e.target.value)} onSearch={() => loadGroupEmployees(selectedGroup, employeeSearch)} enterButton={t('search')} placeholder="Cari employee nama/kode" /></Col><Col><Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Tambah Employee Payroll</Button></Col></Row>
      <Table style={{ marginTop: 16 }} rowKey="payroll_id" loading={loading} columns={employeeColumns} dataSource={employeePayrolls} scroll={{ x: 850 }} pagination={{ pageSize: 10 }} />
    </Card>}

    <Modal title={groupEditing ? 'Edit Group Payroll' : 'Tambah Group Payroll'} open={groupModal} onCancel={() => setGroupModal(false)} onOk={() => groupForm.submit()} confirmLoading={loading} okText="Simpan">
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Satu group mewakili satu periode payroll. Employee payroll akan berada di dalam group ini dan publish dilakukan untuk seluruh group." />
      <Form form={groupForm} layout="vertical" onFinish={saveGroup}>
        <Form.Item name="name" label="Nama Group Payroll" rules={[{ required: true, message: 'Nama group wajib diisi' }]}><Input placeholder="Contoh: Payroll Head Office Oktober 2026" /></Form.Item>
        <Row gutter={12}><Col span={12}><Form.Item name="month" label="Bulan" rules={[{ required: true }]}><Select options={MONTHS.map(m => ({ value: m, label: m }))} /></Form.Item></Col><Col span={12}><Form.Item name="year" label="Tahun" rules={[{ required: true }]}><Select options={getYearOptions().map(y => ({ value: Number(y), label: y }))} /></Form.Item></Col></Row>
      </Form>
    </Modal>

    <PayrollEmployeeFormModal open={formOpen} onClose={() => setFormOpen(false)} editing={editing} form={form} wizardStep={wizardStep} setWizardStep={setWizardStep} savePayroll={savePayroll} employees={employees} payTitles={payTitles} selectedGroup={selectedGroup} formValues={formValues} calcResult={calcResult} calcLoading={calcLoading} estimated={estimated} loading={loading} t={t} />

    <Modal title="Import Employee Payroll" open={importOpen} onCancel={() => setImportOpen(false)} onOk={uploadImport} confirmLoading={loading} okText="Import">
      <Alert showIcon type="info" message="Gunakan template Excel dari group yang sedang dipilih. Nama employee harus sama dengan master employee dan nama kolom komponen mengikuti Payslip Title." style={{ marginBottom: 16 }} />
      <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}><Descriptions.Item label="Group">{selectedGroup?.name}</Descriptions.Item><Descriptions.Item label="Periode">{selectedGroup?.month} {selectedGroup?.year}</Descriptions.Item></Descriptions>
      <Button icon={<DownloadOutlined />} onClick={downloadTemplate} style={{ marginBottom: 16 }}>Download Template Excel</Button>
      <Upload beforeUpload={() => false} accept=".xlsx,.xls" maxCount={1} fileList={fileList} onChange={({ fileList: v }) => setFileList(v)}><Button icon={<UploadOutlined />}>Pilih File Excel</Button></Upload>
    </Modal>

    <Modal title="Log Group Payroll" open={logsOpen} onCancel={() => setLogsOpen(false)} footer={<Button onClick={() => setLogsOpen(false)}>Tutup</Button>} width="min(860px, calc(100vw - 24px))">
      <Table rowKey="payroll_group_log_id" size="small" pagination={{ pageSize: 10 }} dataSource={groupLogs} columns={[
        { title: 'Waktu', dataIndex: 'created_at', render: v => moment(v).format('DD MMM YYYY HH:mm:ss') },
        { title: 'Aktivitas', dataIndex: 'action', render: v => <Tag>{String(v || '').replaceAll('_', ' ').toUpperCase()}</Tag> },
        { title: 'Keterangan', dataIndex: 'description' },
        { title: 'Email', dataIndex: 'blast_email', render: v => v ? 'Ya' : '-' }
      ]} />
    </Modal>

    <SlipGajiModal open={detailOpen} onClose={() => setDetailOpen(false)} detailData={detailData} onExport={exportSlip} t={t} />
  </div>;
}

function PayrollEmployeeFormModal({ open, onClose, editing, form, wizardStep, setWizardStep, savePayroll, employees, payTitles, selectedGroup, formValues, calcResult, calcLoading, estimated, loading, t }) {
  return <Modal title={editing ? 'Edit Employee Payroll' : 'Tambah Employee Payroll'} open={open} onCancel={onClose} width="min(1020px, calc(100vw - 24px))" styles={{ body: { maxHeight: '72vh', overflowY: 'auto' } }} footer={<Space>
    <Button onClick={onClose}>Batal</Button>
    {wizardStep > 0 && <Button onClick={() => setWizardStep(v => v - 1)}>{t('previous')}</Button>}
    {wizardStep < 2 ? <Button type="primary" onClick={async () => { try { if (wizardStep === 0) await form.validateFields(['users_id']); setWizardStep(v => v + 1); } catch (_) {} }}>{t('next')}</Button> : <Button type="primary" loading={loading} onClick={() => form.submit()}>{t('savePayroll')}</Button>}
  </Space>}>
    <Steps current={wizardStep} size="small" style={{ marginBottom: 20 }} items={[{ title: 'Employee' }, { title: 'Komponen Payroll' }, { title: 'Pajak & Review' }]} />
    <Alert type="info" showIcon style={{ marginBottom: 16 }} message={`Group: ${selectedGroup?.name || '-'} • ${selectedGroup?.month || ''} ${selectedGroup?.year || ''}`} description="Periode mengikuti group payroll. Anda hanya perlu memilih employee dan mengisi komponen payroll." />
    <Form form={form} layout="vertical" onFinish={savePayroll}>
      <div style={{ display: wizardStep === 0 ? 'block' : 'none' }}>
        <Form.Item name="users_id" label="Employee" rules={[{ required: true, message: 'Pilih employee' }]}><Select showSearch optionFilterProp="label" options={employees.map(u => ({ value: u.users_id, label: `${u.name}${u.users_code ? ` (${u.users_code})` : ''}` }))} /></Form.Item>
        <Form.Item name="title" label="Judul Slip" extra="Opsional. Jika kosong sistem membuat judul berdasarkan employee dan periode."><Input /></Form.Item>
      </div>
      <div style={{ display: wizardStep === 1 ? 'block' : 'none' }}>
        <Divider orientation="left">Pendapatan & Tunjangan</Divider>
        <Row gutter={12}><Col xs={24} md={8}><Form.Item name="base_salary" label="Gaji Pokok"><InputNumber min={0} style={{ width: '100%' }} {...moneyInputProps} /></Form.Item></Col><Col xs={24} md={8}><Form.Item name="fixed_allowance" label="Tunjangan Tetap"><InputNumber min={0} style={{ width: '100%' }} {...moneyInputProps} /></Form.Item></Col><Col xs={24} md={8}><Form.Item name="other_allowance" label="Tunjangan Lain"><InputNumber min={0} style={{ width: '100%' }} {...moneyInputProps} /></Form.Item></Col></Row>
        <Divider orientation="left">Lembur</Divider>
        <Row gutter={12}><Col xs={24} md={6}><Form.Item name="auto_overtime" label="Overtime Approved" valuePropName="checked"><Switch checkedChildren="AUTO" unCheckedChildren="MANUAL" /></Form.Item></Col><Col xs={12} md={6}><Form.Item name="overtime_hours" label="Jam Lembur"><InputNumber min={0} step={0.25} disabled={formValues.auto_overtime === true} style={{ width: '100%' }} /></Form.Item></Col><Col xs={12} md={6}><Form.Item name="overtime_rate" label="Tarif / Jam"><InputNumber min={0} style={{ width: '100%' }} {...moneyInputProps} /></Form.Item></Col><Col xs={24} md={6}><Card size="small"><Text type="secondary">Nilai lembur</Text><div><Text strong>{currencyFormat(calcResult?.overtime_amount ?? estimated.overtime)}</Text></div></Card></Col></Row>
        <Divider orientation="left">Komponen Tambahan</Divider>
        <Form.List name="components">{(fields, { add, remove }) => <Space direction="vertical" style={{ width: '100%' }}>
          {fields.map(({ key, name, ...rest }) => <Card key={key} size="small"><Row gutter={8}>
            <Col xs={24}><Form.Item {...rest} name={[name, 'name']} label="Slip Name" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={payTitles.map(p => ({ value: p.slip_name || p.name, label: p.slip_name || p.name }))} onChange={value => { const p = payTitles.find(x => (x.slip_name || x.name) === value); const comps = form.getFieldValue('components') || []; comps[name] = { ...(comps[name] || {}), name: value, category: p?.type || 'benefit' }; form.setFieldsValue({ components: [...comps] }); }} /></Form.Item></Col>
            <Col xs={12} md={6}><Form.Item {...rest} name={[name, 'code']} label="Kode"><Input /></Form.Item></Col><Col xs={12} md={6}><Form.Item {...rest} name={[name, 'category']} label="Jenis"><Select options={[{ value: 'benefit', label: 'Pendapatan' }, { value: 'cost', label: 'Potongan' }]} /></Form.Item></Col><Col xs={24} md={6}><Form.Item {...rest} name={[name, 'amount']} label="Nominal"><InputNumber min={0} style={{ width: '100%' }} {...moneyInputProps} /></Form.Item></Col><Col xs={12} md={3}><Form.Item {...rest} name={[name, 'taxable']} label="Taxable" valuePropName="checked"><Switch /></Form.Item></Col><Col xs={12} md={3}><Form.Item {...rest} name={[name, 'tax_deductible']} label="Deductible" valuePropName="checked"><Switch /></Form.Item></Col><Col span={24}><Form.Item {...rest} name={[name, 'desc']} label="Keterangan"><Input /></Form.Item></Col><Col span={24} style={{ textAlign: 'right' }}><Button danger type="text" icon={<MinusCircleOutlined />} onClick={() => remove(name)}>Hapus Komponen</Button></Col>
          </Row></Card>)}
          <div style={{ marginBottom: 64 }}><Button block type="dashed" icon={<PlusOutlined />} onClick={() => add({ category: 'benefit', taxable: true, tax_deductible: false, amount: 0 })}>Tambah Komponen Payroll</Button></div>
        </Space>}</Form.List>
      </div>
      <div style={{ display: wizardStep === 2 ? 'block' : 'none' }}>
        <Divider orientation="left">Potongan & Pajak</Divider>
        <Row gutter={12}><Col xs={24} md={8}><Form.Item name="bpjs_health" label="BPJS Kesehatan"><InputNumber min={0} style={{ width: '100%' }} {...moneyInputProps} /></Form.Item></Col><Col xs={24} md={8}><Form.Item name="bpjs_employment" label="BPJS Ketenagakerjaan"><InputNumber min={0} style={{ width: '100%' }} {...moneyInputProps} /></Form.Item></Col><Col xs={24} md={8}><Form.Item name="other_deduction" label="Potongan Lain"><InputNumber min={0} style={{ width: '100%' }} {...moneyInputProps} /></Form.Item></Col></Row>
        <Row gutter={12}><Col xs={24} md={6}><Form.Item name="auto_pph21" label="PPh21 Otomatis" valuePropName="checked"><Switch checkedChildren="AUTO" unCheckedChildren="MANUAL" /></Form.Item></Col><Col xs={24} md={6}><Form.Item name="is_last_tax_period" label="Masa Pajak Terakhir" valuePropName="checked"><Switch /></Form.Item></Col><Col xs={24} md={6}><Form.Item name="pph21" label="PPh21 Manual"><InputNumber min={0} disabled={formValues.auto_pph21 !== false} style={{ width: '100%' }} {...moneyInputProps} /></Form.Item></Col><Col xs={24} md={12}><Card size="small" loading={calcLoading}><Row gutter={[8, 8]}><Col span={12}><Text type="secondary">Gross</Text><div><Text strong>{currencyFormat(calcResult?.gross_salary ?? estimated.gross)}</Text></div></Col><Col span={12}><Text type="secondary">PPh21</Text><div><Text strong>{currencyFormat(calcResult?.pph21 || 0)}</Text></div></Col><Col span={12}><Text type="secondary">Net Salary</Text><div><Text strong>{currencyFormat(calcResult?.net_salary ?? estimated.netBeforeAutoTax)}</Text></div></Col><Col span={12}><Text type="secondary">Metode</Text><div><Tag>{calcResult?.tax_method || '-'}</Tag></div></Col></Row></Card></Col></Row>
        <Form.Item name="desc" label="Catatan"><Input.TextArea rows={3} /></Form.Item>
      </div>
    </Form>
  </Modal>;
}

function SlipGajiModal({ open, onClose, detailData, onExport, t }) {
  const benefits = (detailData?.pay_details || []).filter(x => x.allowance > 0 && x.pay_title?.type === 'benefit');
  const costs = (detailData?.pay_details || []).filter(x => x.allowance > 0 && x.pay_title?.type === 'cost');
  const totalBenefit = benefits.reduce((a, b) => a + Number(b.allowance || 0), 0);
  const totalCost = costs.reduce((a, b) => a + Number(b.allowance || 0), 0);
  return <Modal open={open} onCancel={onClose} width="min(900px, calc(100vw - 24px))" styles={{ body: { maxHeight: '72vh', overflowY: 'auto' } }} title={<Space><DollarCircleOutlined /><Title level={4} style={{ margin: 0 }}>Slip Gaji Karyawan</Title></Space>} footer={<Space><Button type="primary" icon={<DownloadOutlined />} onClick={() => onExport(detailData)}>Export PDF</Button><Button onClick={onClose}>{t('close')}</Button></Space>}>
    {detailData && <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}><Descriptions.Item label="Employee">{detailData.users?.name || '-'}</Descriptions.Item><Descriptions.Item label="Periode">{detailData.month} {detailData.year}</Descriptions.Item><Descriptions.Item label="Judul">{detailData.title}</Descriptions.Item><Descriptions.Item label="Status"><Tag icon={detailData.status ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={detailData.status ? 'green' : 'orange'}>{detailData.status ? 'Published' : 'Draft'}</Tag></Descriptions.Item></Descriptions>
      {benefits.length > 0 && <Card title="Pendapatan / Tunjangan" size="small"><Table style={{ width: '100%' }} size="small" bordered pagination={false} rowKey="pay_detail_id" dataSource={benefits} columns={[{ title: 'Komponen', render: (_, r) => r.pay_title?.slip_name || r.pay_title?.name }, { title: 'Jumlah', dataIndex: 'allowance', align: 'right', render: currencyFormat }, { title: 'Keterangan', dataIndex: 'desc' }]} /><Paragraph strong style={{ textAlign: 'right', marginTop: 12 }}>Total: {currencyFormat(totalBenefit)}</Paragraph></Card>}
      {costs.length > 0 && <Card title="Potongan" size="small"><Table style={{ width: '100%' }} size="small" bordered pagination={false} rowKey="pay_detail_id" dataSource={costs} columns={[{ title: 'Komponen', render: (_, r) => r.pay_title?.slip_name || r.pay_title?.name }, { title: 'Jumlah', dataIndex: 'allowance', align: 'right', render: currencyFormat }]} /><Paragraph strong style={{ textAlign: 'right', marginTop: 12 }}>Total: {currencyFormat(totalCost)}</Paragraph></Card>}
      <Card><Row justify="space-between" align="middle"><Col><Text strong>GAJI DIBAYARKAN</Text>{detailData.net_salary > 0 && <div><Text italic>{toTerbilang(detailData.net_salary).toUpperCase()} RUPIAH</Text></div>}</Col><Col><Title level={3} style={{ margin: 0 }}>{currencyFormat(detailData.net_salary)}</Title></Col></Row></Card>
    </Space>}
  </Modal>;
}

export default Payroll;
